import React, { useContext, useMemo } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Modal, Wizard } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import Step1Employee from './Step1Employee';
import Step2Classification from './Step2Classification';
import Step3Describe from './Step3Describe';
import { validateStep1, validateStep2, validateStep3 } from './validators';
import useCreateTicketForm, { INITIAL_VALUES } from './hooks/useCreateTicketForm';
import useCreateTicketSubmit from './hooks/useCreateTicketSubmit';

const TitleWrap = styled.span`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 4px;
`;

const TitlePrefix = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
`;

const TitleAccent = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MODAL_Z_INDEX = 120;

const SelectMenuZIndexFix = createGlobalStyle`
  #select.portal-select-input {
    position: relative;
    z-index: ${MODAL_Z_INDEX};
  }
`;

const StyledModal = styled(Modal)`
  .modal-title-content {
    min-width: 0;
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    letter-spacing: 0;
    vertical-align: middle;
  }

  .modal-content {
    min-height: 0;
  }

  .modal-content form {
    overflow: hidden;
    min-height: 0;
  }

  .modal-content form > :not(.buttons) {
    min-height: 0;
  }

  .modal-content .buttons {
    padding: 10px 16px;
  }

  .modal-content .wizard-previous-button,
  .modal-content .wizard-next-button,
  .modal-content .wizard-submit-button {
    height: 40px;
    padding: 0 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const CreateTicketModal = ({ onClose, onCreated, lockedEmployeeId }) => {
  const { t } = useContext(ConfigContext);
  const { mappedBranches } = useBranches();

  const {
    currentPage,
    currentValues,
    updateValues,
    handlePageChange,
    fileUpload,
    resetAttachments
  } = useCreateTicketForm();
  const handleSubmit = useCreateTicketSubmit({ currentValues, onCreated });

  const title = useMemo(() => {
    if (currentPage === 0 || !currentValues.employeeId) {
      return 'help_desk_create_new_ticket';
    }
    const branchName = mappedBranches[currentValues.branchId]?.name || '';
    const target = [currentValues.employeeName, branchName].filter(Boolean).join(', ');
    return (
      <TitleWrap>
        <TitlePrefix>{t('help_desk_create_ticket_for')}</TitlePrefix>
        <TitleAccent>{target}</TitleAccent>
      </TitleWrap>
    );
  }, [currentPage, currentValues, mappedBranches, t]);

  return (
    <>
      <SelectMenuZIndexFix />
      <StyledModal
        zIndex={MODAL_Z_INDEX}
        onClose={onClose}
        title={title}
        size="smallSemiWideLong"
        showConfirmButton={false}
        isPaddingContent={false}
        isScrollableContent={false}
        prompt
        usePortal>
        <Wizard
          initialValues={INITIAL_VALUES}
          onSubmit={handleSubmit}
          onClose={onClose}
          onPageChange={handlePageChange}
          previousText="back"
          doneText="help_desk_create_ticket"
          showDotsNavigation
          keepDirtyOnReinitialize>
          <Wizard.Page validate={validateStep1}>
            <Step1Employee
              onChange={updateValues}
              onResetAttachments={resetAttachments}
              lockedEmployeeId={lockedEmployeeId}
            />
          </Wizard.Page>
          <Wizard.Page validate={validateStep2}>
            <Step2Classification />
          </Wizard.Page>
          <Wizard.Page validate={validateStep3}>
            <Step3Describe accumulated={currentValues} onChange={updateValues} fileUpload={fileUpload} />
          </Wizard.Page>
        </Wizard>
      </StyledModal>
    </>
  );
};

export default CreateTicketModal;
